// frontend/src/components/support/GwinBugReportPanel.jsx

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  AlertCircle,
  Bug,
  CheckCircle2,
  Clock3,
  History,
  Plus,
  RefreshCw,
  Send,
  X,
} from "lucide-react";

import {
  createMyGwinBugReport,
  fetchMyGwinBugReports,
} from "../../lib/support/gwinBugSupport";


const STATUS_STYLES = {
  open:
    "border-red-200 bg-red-50 text-red-700",

  acknowledged:
    "border-amber-200 bg-amber-50 text-amber-700",

  resolved:
    "border-green-200 bg-green-50 text-green-700",

  closed:
    "border-gray-200 bg-gray-100 text-gray-700",
};


function formatStatus(
  status
) {
  switch (
    String(
      status || ""
    ).toLowerCase()
  ) {
    case "acknowledged":
      return "Acknowledged";

    case "resolved":
      return "Resolved";

    case "closed":
      return "Closed";

    default:
      return "Open";
  }
}


function formatDateTime(
  value
) {
  if (!value) {
    return "Not available";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return value;
  }

  return new Intl.DateTimeFormat(
    "en-ZA",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  ).format(date);
}


function StatusBadge({
  status,
}) {
  const cleanStatus =
    String(
      status || "open"
    ).toLowerCase();

  return (
    <span
      className={[
        "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold",
        STATUS_STYLES[
          cleanStatus
        ] ||
          STATUS_STYLES.open,
      ].join(" ")}
    >
      {formatStatus(
        cleanStatus
      )}
    </span>
  );
}


export default function GwinBugReportPanel({
  source = "app",
}) {
  const [
    reports,
    setReports,
  ] =
    useState([]);

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    error,
    setError,
  ] =
    useState("");

  const [
    notice,
    setNotice,
  ] =
    useState("");

  const [
    modalOpen,
    setModalOpen,
  ] =
    useState(false);

  const [
    subject,
    setSubject,
  ] =
    useState("");

  const [
    message,
    setMessage,
  ] =
    useState("");

  const [
    pagePath,
    setPagePath,
  ] =
    useState("");

  const [
    sending,
    setSending,
  ] =
    useState(false);


  const loadReports =
    useCallback(async () => {
      try {
        setLoading(true);
        setError("");

        const rows =
          await fetchMyGwinBugReports();

        setReports(
          Array.isArray(
            rows
          )
            ? rows
            : []
        );
      } catch (
        loadError
      ) {
        console.error(
          "Gwin bug reports loading failed:",
          loadError
        );

        setReports([]);

        setError(
          loadError?.message ||
            "Your technical support reports could not be loaded."
        );
      } finally {
        setLoading(false);
      }
    }, []);


  useEffect(() => {
    loadReports();
  }, [loadReports]);


  const activeReports =
    useMemo(
      () =>
        reports.filter(
          (report) =>
            [
              "open",
              "acknowledged",
            ].includes(
              report.status
            )
        ),
      [reports]
    );


  const recentReports =
    useMemo(
      () =>
        reports.slice(
          0,
          5
        ),
      [reports]
    );


  function openModal() {
    setError("");
    setNotice("");

    setSubject("");
    setMessage("");
    setPagePath("");

    setModalOpen(true);
  }


  function closeModal() {
    if (sending) {
      return;
    }

    setModalOpen(false);

    setSubject("");
    setMessage("");
    setPagePath("");
  }


  async function submitBug(
    event
  ) {
    event.preventDefault();

    const cleanSubject =
      String(
        subject || ""
      ).trim();

    const cleanMessage =
      String(
        message || ""
      ).trim();

    const cleanPagePath =
      String(
        pagePath || ""
      ).trim();

    if (
      cleanSubject.length <
      3
    ) {
      setError(
        "Please enter a subject of at least 3 characters."
      );

      return;
    }

    if (
      cleanMessage.length <
      10
    ) {
      setError(
        "Please provide a little more detail about the problem."
      );

      return;
    }

    try {
      setSending(true);
      setError("");
      setNotice("");

      await createMyGwinBugReport(
        {
          subject:
            cleanSubject,

          message:
            cleanMessage,

          pagePath:
            cleanPagePath,

          metadata: {
            source,

            submission_path:
              typeof window !==
              "undefined"
                ? `${window.location.pathname}${window.location.search}`
                : null,

            user_agent:
              typeof navigator !==
              "undefined"
                ? navigator.userAgent
                : null,
          },
        }
      );

      setModalOpen(
        false
      );

      setSubject("");
      setMessage("");
      setPagePath("");

      setNotice(
        "Your technical issue has been sent to Gwin Support."
      );

      await loadReports();
    } catch (
      submitError
    ) {
      console.error(
        "Gwin bug report submission failed:",
        submitError
      );

      setError(
        submitError?.message ||
          "Your technical issue could not be submitted."
      );
    } finally {
      setSending(false);
    }
  }


  return (
    <>
      <section className="rounded-3xl border border-orange-100 bg-white p-6 shadow-sm sm:p-8">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-50">
              <Bug className="h-6 w-6 text-[#FF914D]" />
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#FF914D]">
                Gwin · Technical support
              </p>

              <h2 className="mt-2 text-2xl font-black text-gray-900">
                Report a technical issue
              </h2>

              <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-600">
                If something in Gwin is
                broken, not loading,
                showing an error, or not
                behaving as expected,
                send the issue directly
                to the Gwin Admin team.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={
              openModal
            }
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-[#FF914D] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#E9782F]"
          >
            <Plus className="h-4 w-4" />

            Report a bug
          </button>
        </div>


        {error && (
          <div className="mt-5 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />

            <span>
              {error}
            </span>
          </div>
        )}


        {notice && (
          <div className="mt-5 flex items-start gap-3 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />

            <span>
              {notice}
            </span>
          </div>
        )}


        <div className="mt-6 border-t border-gray-100 pt-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-black text-gray-900">
                Your technical reports
              </h3>

              <p className="mt-1 text-sm text-gray-500">
                {activeReports.length ===
                0
                  ? "You have no open technical issues."
                  : activeReports.length ===
                      1
                    ? "1 technical issue is still being handled."
                    : `${activeReports.length} technical issues are still being handled.`}
              </p>
            </div>

            <button
              type="button"
              onClick={
                loadReports
              }
              disabled={
                loading
              }
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-bold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw
                className={[
                  "h-4 w-4",
                  loading
                    ? "animate-spin"
                    : "",
                ].join(
                  " "
                )}
              />

              Refresh
            </button>
          </div>


          {loading ? (
            <div className="mt-5 flex items-center gap-2 text-sm font-semibold text-gray-500">
              <RefreshCw className="h-4 w-4 animate-spin text-[#FF914D]" />

              Loading technical reports…
            </div>
          ) : recentReports.length ===
            0 ? (
            <div className="mt-5 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-5">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />

                <div>
                  <p className="text-sm font-bold text-gray-800">
                    No technical issues reported
                  </p>

                  <p className="mt-1 text-sm text-gray-500">
                    Any bugs you report
                    to Gwin will appear
                    here.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-5 space-y-3">
              {recentReports.map(
                (report) => (
                  <div
                    key={
                      report.report_id
                    }
                    className="rounded-2xl border border-gray-100 bg-gray-50 p-4"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-bold text-gray-900">
                          {
                            report.subject
                          }
                        </p>

                        <p className="mt-1 text-xs text-gray-500">
                          {formatDateTime(
                            report.created_at
                          )}
                        </p>
                      </div>

                      <StatusBadge
                        status={
                          report.status
                        }
                      />
                    </div>

                    {report.page_path && (
                      <p className="mt-3 break-all text-xs font-semibold text-gray-500">
                        Area:{" "}
                        {
                          report.page_path
                        }
                      </p>
                    )}
                  </div>
                )
              )}
            </div>
          )}


          {reports.length > 5 && (
            <div className="mt-4 flex items-center gap-2 text-xs font-semibold text-gray-500">
              <History className="h-4 w-4" />

              Showing your 5 most
              recent technical reports.
            </div>
          )}
        </div>
      </section>


      {modalOpen && (
        <div
          className="fixed inset-0 z-[150] flex items-center justify-center bg-black/40 px-4 py-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gwin-bug-report-title"
          onMouseDown={(
            event
          ) => {
            if (
              event.target ===
                event.currentTarget &&
              !sending
            ) {
              closeModal();
            }
          }}
        >
          <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-5 sm:px-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#FF914D]">
                  Gwin technical support
                </p>

                <h2
                  id="gwin-bug-report-title"
                  className="mt-2 text-xl font-black text-gray-900"
                >
                  Report a bug
                </h2>

                <p className="mt-1 text-sm text-gray-500">
                  Tell us what happened
                  and where you noticed
                  the problem.
                </p>
              </div>

              <button
                type="button"
                onClick={
                  closeModal
                }
                disabled={
                  sending
                }
                className="rounded-xl p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50"
                aria-label="Close bug report"
              >
                <X className="h-5 w-5" />
              </button>
            </div>


            <form
              onSubmit={
                submitBug
              }
              className="space-y-5 px-5 py-6 sm:px-6"
            >
              {error && (
                <div
                  role="alert"
                  className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                >
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />

                  <span>
                    {error}
                  </span>
                </div>
              )}


              <div>
                <label className="block text-sm font-black text-gray-900">
                  Subject
                </label>

                <input
                  type="text"
                  value={
                    subject
                  }
                  onChange={(
                    event
                  ) =>
                    setSubject(
                      event
                        .target
                        .value
                        .slice(
                          0,
                          120
                        )
                    )
                  }
                  disabled={
                    sending
                  }
                  maxLength={
                    120
                  }
                  required
                  placeholder="For example: Cohort page will not load"
                  className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/10"
                />

                <p className="mt-1 text-right text-xs text-gray-400">
                  {subject.length}
                  /120
                </p>
              </div>


              <div>
                <label className="block text-sm font-black text-gray-900">
                  Page or area affected
                </label>

                <input
                  type="text"
                  value={
                    pagePath
                  }
                  onChange={(
                    event
                  ) =>
                    setPagePath(
                      event
                        .target
                        .value
                        .slice(
                          0,
                          500
                        )
                    )
                  }
                  disabled={
                    sending
                  }
                  maxLength={
                    500
                  }
                  placeholder="For example: Cohorts, Reports, or /partner/cohorts"
                  className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/10"
                />

                <p className="mt-1 text-xs text-gray-500">
                  Optional, but it helps
                  us locate the issue.
                </p>
              </div>


              <div>
                <label className="block text-sm font-black text-gray-900">
                  What happened?
                </label>

                <textarea
                  value={
                    message
                  }
                  onChange={(
                    event
                  ) =>
                    setMessage(
                      event
                        .target
                        .value
                        .slice(
                          0,
                          4000
                        )
                    )
                  }
                  disabled={
                    sending
                  }
                  maxLength={
                    4000
                  }
                  rows={7}
                  required
                  placeholder="Explain what you were trying to do, what happened, and what you expected to happen."
                  className="mt-2 w-full resize-y rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/10"
                />

                <p className="mt-1 text-right text-xs text-gray-400">
                  {message.length}
                  /4000
                </p>
              </div>


              <div className="rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
                <div className="flex items-start gap-3">
                  <Clock3 className="mt-0.5 h-5 w-5 shrink-0 text-[#FF914D]" />

                  <p className="text-sm leading-6 text-gray-600">
                    This sends the issue
                    directly to the Gwin
                    Admin Support inbox.
                    Your Partner learner
                    support requests remain
                    separate.
                  </p>
                </div>
              </div>


              <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-5 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={
                    closeModal
                  }
                  disabled={
                    sending
                  }
                  className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-bold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={
                    sending
                  }
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#FF914D] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sending ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}

                  {sending
                    ? "Sending…"
                    : "Send to Gwin"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}